import React from "react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "styled-components";
import { icons, colors, colors2, TooltipV2, ButtonGhostCompact } from "oolib";
import {
  StyledStatCardGroup,
  StyledStatCard,
  StyledTextGroup,
  StyledValue,
  StyledTitle,
  StyledSubline,
  StyledProgressFill,
} from "./styled";

const { Info } = icons;
// The title icon, sized to sit with the title's text line.
const TITLE_ICON_SIZE = 16;

export function StatCardGroup({ children }: { children: React.ReactNode }) {
  return <StyledStatCardGroup>{children}</StyledStatCardGroup>;
}

export function StatCard({
  icon,
  iconComp,
  title,
  value,
  subline,
  sublineColor,
  bg,
  progress = 0,
  infoTooltip,
  infoTooltipOn = "title",
  link,
}: {
  // An oolib icon by name, or (`iconComp`) any icon component — for the
  // phosphor icons oolib's set doesn't carry.
  icon?: string;
  iconComp?: React.ComponentType<{ size?: number; color?: string; weight?: string; style?: React.CSSProperties }>;
  title: string;
  value: string;
  // A short reading under the title ("Severe and rising. Act fast.").
  subline?: string;
  sublineColor?: string;
  // A tint behind the whole card (a zone's field colour).
  bg?: string;
  progress?: number;
  infoTooltip?: React.ReactNode;
  // Where the (i) sits: after the title, or after the subline when the
  // subline is the thing it explains ("vs. manual tagging (i)").
  infoTooltipOn?: "title" | "subline";
  // A way out under the number, to the page it counts ("View All" →
  // /datasets): the widgets' compact ghost button, in the link blue.
  link?: { label: string; to: string };
}) {
  const navigate = useNavigate();
  // The same blue OKELink draws in: the theme's link colour, else oolib's.
  const theme = useTheme() as { colors?: { primaryColorText?: string } } | undefined;
  const linkBlue = theme?.colors?.primaryColorText || colors.primaryColorText;
  const IconComp = iconComp || (icon ? icons[icon] : null);
  const infoIcon = infoTooltip ? (
    <TooltipV2
      primaryContent={{
        layout: "custom",
        render: <div style={{ padding: "8px 10px", maxWidth: 300 }}>{infoTooltip}</div>,
      }}
      trigger="hover"
      placement="bottom"
    >
      <Info size={14} color={colors2.grey40} style={{ cursor: "pointer", display: "block" }} />
    </TooltipV2>
  ) : null;
  const linkButton = link ? (
    <ButtonGhostCompact S value={link.label} iconAfter="ArrowRight" iconColor={linkBlue} style={{ color: linkBlue }} onClick={() => navigate(link.to)} />
  ) : null;
  return (
    <StyledStatCard style={bg ? { background: bg } : undefined}>
      {/* The label leads: icon and title as one unit, the icon sized to the
          title's text and in its grey, then the number below. */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {IconComp && <IconComp size={TITLE_ICON_SIZE} color={colors2.grey60} weight="bold" style={{ flexShrink: 0 }} />}
        <StyledTitle>{title}</StyledTitle>
        {infoTooltipOn === "title" && infoIcon}
      </div>
      <StyledTextGroup>
        <StyledValue>{value}</StyledValue>
        {subline && (
          <StyledSubline style={{ display: "flex", alignItems: "center", gap: 4, ...(sublineColor ? { color: sublineColor } : {}) }}>
            {subline}
            {infoTooltipOn === "subline" && infoIcon}
          </StyledSubline>
        )}
        {link && (
          // The compact button pads itself 4px 10px. No top margin, so its
          // own 4px puts the text on the subline's line (the subline sits
          // 0.4rem under the number); pulled left by its side padding so the
          // text starts on the card's left edge.
          <div style={{ marginLeft: -10 }}>{linkButton}</div>
        )}
      </StyledTextGroup>
      <StyledProgressFill data-progress={progress} style={{ "--progress": progress } as React.CSSProperties} />
    </StyledStatCard>
  );
}
